// src/components/QuizGrid/QuizGrid.js

import React, { useState } from "react";
import styled from "styled-components";
import QuizGridModal from "./QuizGridModal";
import { darken, QuizScreen } from "../../../types/GeneralStyles"; // Assuming QuizScreen is a styled component
import QuestionPublicLink from "../../PublicLink/PublicLink";
import ReactMarkdown from "react-markdown";
import GetScore from "./QuizGridScore";

// Styled Components for the Grid Layout
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.columns}, 1fr);
  gap: 20px;
  padding: 20px;
  
  @media only screen and (max-width: 768px) {
    grid-template-columns: repeat(${props => Math.max(props.columns - 1, 1)}, 1fr);
  }

  @media only screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const GridItem = styled.div`
  background-color: ${props => props.background_color || "#ffffff"};
  color: ${props => props.text_color || "#000000"};
  padding: 20px;
  border-radius: 10px;
  cursor: pointer;
  position:relative;
  box-shadow: 0 4px 6px -1px rgba(0, 29, 41, 0.1), 0 2px 4px -2px rgba(0, 29, 41, 0.1);  
  align-items: center;
  justify-content: center;
  text-align: center;
  &:hover {
   background-color: ${props => darken(props.background_color,15) || darken("#ffffff",15)};
  }
 
`;

const GridScore = styled.div`
  display:flex;
  background-color: white;
  color: black;
  border-radius: 50%;
  width: 40px;
  height:40px;
  font-weight:bold;
  justify-content:center;
  align-items:center;
  position:absolute;
  right:10px;
  top:10px;
`;

const HeaderFooter = styled.div`
  text-align: center;
  margin-bottom: 10px;
  color: ${props => props.text_color || "#000000"};
`;

const QuizGrid = ({ shape, index, overViewPage, quiz, renderComponent }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentQuizItem, setCurrentQuizItem] = useState(null);
  const [currentPopupType, setCurrentPopupType] = useState(null);
  const [popupCloseButton, setPopupCloseButton] = useState(null);
  const [refreshScores, setRefreshScores] = useState(0);


  // Extract the number of columns from the shape
  const columns = shape.columns || 2;

  // Extract quiz_grid_order
  const gridOrder = shape.quiz_grid_order || [];

  // Function to handle item click
  const handleItemClick = (quiz_item_id, popUpType) => {
    // Find the quiz item data from quiz.Quizes or quiz.quiz_results
    const quizItem = quiz.Quizes.find(q => q.quiz_item_id === quiz_item_id) || 
                     quiz.quiz_results.find(r => r.results_item_id === quiz_item_id);
    if(popUpType) {
      if(popUpType === 'fullscreen' 
        || popUpType === 'fullscreen_with_close') {
        setCurrentPopupType('fullscreen');
      } else if(popUpType === 'allmost_fullscreen' 
        || popUpType === 'allmost_fullscreen_with_close') {
        setCurrentPopupType('allmost_fullscreen');
      } else if(popUpType === 'small' 
        || popUpType === 'small_with_close') {
        setCurrentPopupType('small');
      }
    }else{
      setCurrentPopupType('allmost_fullscreen');
    }

    if(popUpType === 'fullscreen_with_close' 
    || popUpType === 'allmost_fullscreen_with_close'
    || popUpType === 'small_with_close') {
      setPopupCloseButton(true);
    }else{
      setPopupCloseButton(false);
    }
   
     
    if (quizItem) {
      setCurrentQuizItem(quizItem);
      setIsModalOpen(true);
    } else {
      console.error(`Quiz item with ID "${quiz_item_id}" not found.`);
    }
  };

  // Function to close the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentQuizItem(null);
    // **Update Refresh State**
    setRefreshScores(prev => prev + 1);
  };

  return (
    <QuizScreen
      bgColor={shape.theme_color}
      bgVisible={overViewPage}
      id={shape.quiz_shape_id}
      key={shape.id}
    
    >
      <QuestionPublicLink
         overViewPage={overViewPage}
        type="shape"
        quiz_item_id={shape.quiz_shape_id}
      />
      {/* Optional: Header */}
      {shape.quiz_item_id_as_header && (
        <HeaderFooter text_color={quiz.Quizes.find(q => q.quiz_item_id === shape.quiz_item_id_as_header)?.title_colour || "#000"}>
          {renderComponent(
            quiz.Quizes.find(q => q.quiz_item_id === shape.quiz_item_id_as_header),
            `${shape.quiz_item_id_as_header}-header`,
            quiz,
            () => {} // No navigation button needed for header
          )}
        </HeaderFooter>
      )}

      {/* Grid Container */}
      <GridContainer columns={columns}>
        {gridOrder.map(item => (
          
          <GridItem
            key={item.id}
            background_color={item.background_color}
            text_color={item.text_color}
            onClick={() => handleItemClick(item.quiz_item_id, item.popup_type)}
          >
            {item.result_quiz_item_id && (<GridScore><GetScore quiz={quiz} quizItemSet={item} refresh={refreshScores} /></GridScore>)}
            {/* Customize what to display inside each grid item */}
            {/* For example, using the title or media from the quiz item */}
            <ReactMarkdown>{quiz.Quizes.find(q => q.quiz_item_id === item.quiz_item_id_as_banner_text)?.description || 
             quiz.quiz_results.find(r => r.results_item_id === item.quiz_item_id_as_banner_text)?.description || ""}
             </ReactMarkdown>
          </GridItem>
        ))}
      </GridContainer>

      {/* Optional: Footer */}
      {shape.quiz_item_id_as_footer && (
        <HeaderFooter text_color={quiz.Quizes.find(q => q.quiz_item_id === shape.quiz_item_id_as_footer)?.title_colour || "#000"}>
          {renderComponent(
            quiz.Quizes.find(q => q.quiz_item_id === shape.quiz_item_id_as_footer),
            `${shape.quiz_item_id_as_footer}-footer`,
            quiz,
            () => {} // No navigation button needed for footer
          )}
        </HeaderFooter>
      )}

      {/* Modal for displaying quiz item content */}
      {isModalOpen && currentQuizItem && (
        <QuizGridModal onClose={closeModal} popupType={currentPopupType} closeButton={popupCloseButton} bgColor={shape.theme_color} txtColor={shape.text_color}>
          <div>
            {renderComponent(currentQuizItem, `modal-${currentQuizItem.quiz_item_id}`, quiz, closeModal)}
          </div>
        </QuizGridModal>
      )}
    </QuizScreen>
  );
};

export default QuizGrid;
